<template>
  <div class="modal-card" style="min-width: 480px">
    <header class="modal-card-head">
      <p class="modal-card-title">
        {{ $t('pages.loads.mass_export.title') }}
      </p>
    </header>
    <section class="modal-card-body">
      <b-field grouped>
        <b-field :label="$t('pages.loads.mass_export.from')" expanded>
          <b-datepicker
            v-model="form.from"
            icon="calendar"
            :max-date="form.to"
            :placeholder="$t('global.click_to_select')"
          />
        </b-field>
        <b-field :label="$t('pages.loads.mass_export.to')" expanded>
          <b-datepicker
            v-model="form.to"
            icon="calendar"
            :min-date="form.from"
            :placeholder="$t('global.click_to_select')"
          />
        </b-field>
      </b-field>
      <b-field :label="$t('pages.loads.mass_export.shipment_type')">
        <b-select v-model="form.direction" expanded>
          <option value="outgoing">
            {{ $t('pages.loads.mass_export.outgoing') }}
          </option>
          <option value="incoming">
            {{ $t('pages.loads.mass_export.incoming') }}
          </option>
          <option value="both">
            {{ $t('pages.loads.mass_export.both') }}
          </option>
        </b-select>
      </b-field>
      <b-field>
        <b-checkbox v-model="form.repeatHeaders">
          {{ $t('pages.loads.mass_export.repeat_headers') }}
        </b-checkbox>
      </b-field>
      <b-field v-if="isExporting && progress.total" :label="$t('pages.loads.mass_export.progress')">
        <b-progress
          :max="progress.total"
          :value="progress.fetched"
          type="is-info"
          show-value
        >
          {{ progress.fetched }} / {{ progress.total }}
        </b-progress>
      </b-field>
    </section>
    <footer class="modal-card-foot">
      <b-button
        type="is-info"
        icon-left="file-csv"
        :loading="isExporting"
        :disabled="!form.from || !form.to"
        @click="exportCsv"
      >
        {{ $t('pages.loads.mass_export.export') }}
      </b-button>
    </footer>
  </div>
</template>

<script>
import { saveAs } from 'file-saver'
import Plant from '@/services/v2/plant'
import Shipment from '@/services/v2/shipment'

// movements are fetched one shipment at a time, keep a few requests in flight without flooding the api
const CONCURRENT_REQUESTS = 6
const SEPARATOR = ';'
const REQUIREMENTS = {
  outgoing: ['DEL', 'TRFO'],
  incoming: ['COL', 'TRFI', 'NCR']
}

const escapeCsv = (value) => {
  // consignee names regularly contain commas and line breaks, drop them so the file stays
  // readable whatever delimiter the reader assumes
  const text = (value === null || value === undefined ? '' : String(value))
    .replace(/,/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  return /[";]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

export default {
  props: {
    plantId: {
      default: '',
      type: String
    }
  },
  data: function () {
    const lastMonth = this.$dayjs().subtract(1, 'month')
    return {
      isExporting: false,
      progress: {
        fetched: 0,
        total: 0
      },
      form: {
        from: lastMonth.startOf('month').toDate(),
        to: lastMonth.endOf('month').toDate(),
        direction: 'both',
        repeatHeaders: false
      }
    }
  },
  computed: {
    requirements () {
      return this.form.direction === 'both'
        ? [...REQUIREMENTS.outgoing, ...REQUIREMENTS.incoming]
        : REQUIREMENTS[this.form.direction]
    },
    columns () {
      const label = (key) => this.$t(`pages.loads.mass_export.columns.${key}`)
      return [
        { label: label('scan_date'), value: (material) => this.$dayjs(material.scannedAt).format('YYYY-MM-DD HH:mm:ss') },
        { label: label('barcode'), value: (material) => material.barcode },
        { label: label('crate_type'), value: (material) => material.material },
        { label: label('shipment_number'), value: (material, shipment) => shipment.shipmentNumber },
        { label: label('document_number'), value: (material, shipment) => shipment.deliveryNumber || shipment.collectNumber },
        { label: label('type'), value: (material, shipment) => shipment.requirement },
        { label: label('counterparty'), value: (material, shipment) => this.counterparty(shipment) }
      ]
    }
  },
  methods: {
    exportCsv: async function () {
      const from = this.$dayjs(this.form.from).format('YYYY-MM-DD')
      const to = this.$dayjs(this.form.to).format('YYYY-MM-DD')
      let loadingToast = null

      this.isExporting = true
      this.progress = { fetched: 0, total: 0 }
      try {
        if (from > to) throw Error(this.$t('pages.loads.mass_export.invalid_range'))

        const shipments = await this.getShipmentsInRange(from, to)
        if (!shipments.length) throw Error(this.$t('pages.loads.mass_export.no_shipment'))

        this.progress.total = shipments.length
        loadingToast = this.$buefy.toast.open({
          message: this.$t('pages.loads.mass_export.fetching', { count: shipments.length }),
          type: 'is-info',
          position: 'is-bottom',
          indefinite: true
        })

        const { rows, failed } = await this.getMaterials(shipments)
        if (!rows.length) throw Error(this.$t('pages.loads.mass_export.no_material'))

        const csvBlob = new Blob(['﻿' + this.buildCsv(rows)], { type: 'text/csv;charset=utf-8' })
        saveAs(csvBlob, `${this.plantId}_materials_${from}_${to}.csv`)

        loadingToast.close()
        loadingToast = null
        this.notifyResult(rows, failed)
        this.$emit('on-close')
      } catch (error) {
        if (loadingToast) {
          loadingToast.close()
          loadingToast = null
        }
        this.$buefy.toast.open({ type: 'is-danger', message: error.message || error, duration: 5000 })
      } finally {
        if (loadingToast) loadingToast.close()
        this.isExporting = false
      }
    },
    getShipmentsInRange: async function (from, to) {
      // the api filters dateAtPlant with a strict greater than, hence the extra day
      const shipments = await Plant.getShipments(this.plantId, {
        from: this.$dayjs(from).subtract(1, 'day').format('YYYYMMDD')
      })

      return (shipments || [])
        .filter(e => e.dateAtPlant >= from && e.dateAtPlant <= to && this.requirements.includes(e.requirement))
        .sort((a, b) => (a.dateAtPlant + a.vakey).localeCompare(b.dateAtPlant + b.vakey))
    },
    getMaterials: async function (shipments) {
      const rows = new Array(shipments.length)
      const failed = []
      let cursor = 0

      const worker = async () => {
        while (cursor < shipments.length) {
          const index = cursor++
          const shipment = shipments[index]
          try {
            const movements = await Shipment.getMovements(shipment.vakey)
            rows[index] = {
              shipment,
              materials: movements
                .filter(e => e.barcode)
                .sort((a, b) => new Date(a.scannedAt) - new Date(b.scannedAt))
            }
          } catch (error) {
            rows[index] = { shipment, materials: [] }
            failed.push(shipment.vakey)
          }
          this.progress.fetched += 1
        }
      }

      await Promise.all(Array.from({ length: Math.min(CONCURRENT_REQUESTS, shipments.length) }, worker))

      return { rows: rows.filter(e => e.materials.length), failed }
    },
    buildCsv: function (rows) {
      const header = this.columns.map(e => e.label)
      const lines = this.form.repeatHeaders ? [] : [header]

      for (const { shipment, materials } of rows) {
        if (this.form.repeatHeaders) lines.push([], header)
        for (const material of materials) {
          lines.push(this.columns.map(e => e.value(material, shipment)))
        }
      }

      return lines.map(line => line.map(escapeCsv).join(SEPARATOR)).join('\n')
    },
    counterparty: function (shipment) {
      const names = [...new Set((shipment.loads || []).map(e => e.consigneeName).filter(Boolean))]
      return names.length ? names.join(' / ') : shipment.consigneeName
    },
    notifyResult: function (rows, failed) {
      const count = rows.reduce((acc, e) => acc + e.materials.length, 0)
      const message = failed.length
        ? `${this.$t('pages.loads.mass_export.done', { count })} - ${this.$t('pages.loads.mass_export.partial', { count: failed.length })}`
        : this.$t('pages.loads.mass_export.done', { count })

      this.$buefy.toast.open({
        type: failed.length ? 'is-warning' : 'is-success',
        position: 'is-bottom',
        duration: 5000,
        message
      })
    }
  }
}
</script>
